import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEvaluation } from '../contexts/EvaluationContext';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Award,
  RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';

const TakeQuiz = () => {
  const { evaluationId } = useParams();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');
  const navigate = useNavigate();
  const { getEvaluationById, getMyAttempts, submitAttempt } = useEvaluation();

  const [evaluation, setEvaluation] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Quiz state
  const [phase, setPhase] = useState('intro'); // 'intro' | 'taking' | 'results'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [lastResult, setLastResult] = useState(null); // résultat de la dernière tentative

  // Chargement initial
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      const [evalRes, attRes] = await Promise.all([
        getEvaluationById(evaluationId),
        getMyAttempts(evaluationId),
      ]);
      if (!evalRes.success) {
        setError(evalRes.error || 'Évaluation introuvable');
      } else {
        setEvaluation(evalRes.evaluation);
      }
      if (attRes.success) setAttempts(attRes.attempts || []);
      setLoading(false);
    };
    load();
  }, [evaluationId, getEvaluationById, getMyAttempts]);

  // Timer (en cours d'évaluation)
  useEffect(() => {
    if (phase !== 'taking' || timeLeft === null) return;
    if (timeLeft <= 0) {
      handleSubmitQuiz(true);
      return;
    }
    const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, phase]);

  const startQuiz = useCallback(() => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setStartedAt(Date.now());
    if (evaluation?.time_limit_minutes) {
      setTimeLeft(evaluation.time_limit_minutes * 60);
    } else {
      setTimeLeft(null);
    }
    setPhase('taking');
  }, [evaluation]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [String(questionId)]: answer }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < (evaluation?.questions?.length || 0) - 1) {
      setCurrentQuestionIndex(i => i + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(i => i - 1);
  };

  const handleSubmitQuiz = useCallback(async (autoSubmit = false) => {
    if (!autoSubmit && !showConfirmSubmit) {
      setShowConfirmSubmit(true);
      return;
    }
    setShowConfirmSubmit(false);
    setIsSubmitting(true);

    const questions = evaluation?.questions || [];
    const submissionAnswers = questions.map(q => ({
      questionId: q.id,
      answer: answers[String(q.id)] !== undefined ? answers[String(q.id)] : null,
    }));

    const timeSpent = startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;

    const result = await submitAttempt(evaluationId, submissionAnswers, timeSpent);

    if (result.success) {
      setAttempts(prev => [result.attempt, ...prev]);
      setLastResult(result);
      setPhase('results');
      if (result.certificateIssuedNow) {
        toast.success('Félicitations ! Votre certificat a été émis automatiquement !', { duration: 6000 });
      } else if (result.passed) {
        toast.success('Module validé !');
        if (result.certificateEligibleReason && !result.certificatePaiementRequis) {
          toast(result.certificateEligibleReason, { duration: 9000 });
        }
      } else {
        toast.error(`Score insuffisant — requis : ${evaluation.passing_score}%`);
      }
    } else {
      toast.error(result.error || 'Erreur lors de la soumission');
    }
    setIsSubmitting(false);
  }, [
    showConfirmSubmit, evaluation, answers, evaluationId,
    submitAttempt, startedAt,
  ]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ── Écrans ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement de l'évaluation…</p>
        </div>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {error || 'Évaluation introuvable'}
          </h1>
          <button
            onClick={() => navigate(courseId ? `/course/${courseId}/learn` : -1)}
            className="mt-4 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retour au cours
          </button>
        </div>
      </div>
    );
  }

  const questions = evaluation.questions || [];
  const attemptsCount = attempts.length;
  const maxAttempts = evaluation.max_attempts;
  const canRetry = !maxAttempts || attemptsCount < maxAttempts;
  const bestAttempt = attempts.length > 0
    ? attempts.reduce((best, a) => (Number(a.percentage) > Number(best.percentage) ? a : best), attempts[0])
    : null;

  // ── Phase INTRO ──────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate(courseId ? `/course/${courseId}/learn` : -1)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 dark:hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Retour au cours
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{evaluation.title}</h1>
              {evaluation.description && (
                <p className="text-gray-600 dark:text-gray-400">{evaluation.description}</p>
              )}
            </div>

            {/* Infos */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-primary">{questions.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Questions</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-primary">{evaluation.passing_score}%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Score requis</div>
              </div>
              {evaluation.time_limit_minutes && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary">{evaluation.time_limit_minutes} min</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Durée limite</div>
                </div>
              )}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-primary">
                  {maxAttempts ? `${attemptsCount}/${maxAttempts}` : attemptsCount}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Tentatives</div>
              </div>
            </div>

            {/* Instructions */}
            {evaluation.instructions && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-1">Instructions</h3>
                    <p className="text-blue-800 dark:text-blue-300 text-sm">{evaluation.instructions}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Meilleur score précédent */}
            {bestAttempt && (
              <div className={`rounded-lg p-4 mb-6 flex items-center gap-3 ${
                bestAttempt.passed
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
              }`}>
                {bestAttempt.passed
                  ? <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  : <XCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />}
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    Meilleur score : {Math.round(Number(bestAttempt.percentage))}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {bestAttempt.passed ? 'Module validé' : `Requis : ${evaluation.passing_score}%`}
                  </div>
                </div>
              </div>
            )}

            {canRetry ? (
              <button
                onClick={startQuiz}
                className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary/90 font-medium text-lg"
              >
                {attemptsCount > 0 ? 'Recommencer l\'évaluation' : 'Commencer l\'évaluation'}
              </button>
            ) : (
              <div className="text-center py-4">
                <p className="text-red-600 dark:text-red-400 font-medium">
                  Nombre maximum de tentatives atteint ({maxAttempts})
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Phase TAKING ─────────────────────────────────────────────────────────
  if (phase === 'taking') {
    const currentQuestion = questions[currentQuestionIndex];
    const answeredCount = Object.keys(answers).length;
    const progressPct = questions.length > 0
      ? Math.round(((currentQuestionIndex + 1) / questions.length) * 100)
      : 0;

    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">{evaluation.title}</h1>
              </div>
              {timeLeft !== null && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold ${
                  timeLeft < 300 ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                }`}>
                  <Clock className="w-4 h-4" />
                  {formatTime(timeLeft)}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Question {currentQuestionIndex + 1} / {questions.length}</span>
              <span>{answeredCount} réponse{answeredCount > 1 ? 's' : ''} donnée{answeredCount > 1 ? 's' : ''}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          {/* Question */}
          {currentQuestion && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-4">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex-1 pr-4">
                  {currentQuestion.question || currentQuestion.text}
                </h2>
                {currentQuestion.points && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {currentQuestion.points} pt{currentQuestion.points > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Choix multiples */}
              {(['multiple_choice', 'multiple-choice', 'true_false', 'true-false'].includes(currentQuestion.type)) && (
                <div className="space-y-3">
                  {(currentQuestion.options || (['true_false', 'true-false'].includes(currentQuestion.type) ? ['Vrai', 'Faux'] : [])).map((opt, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                        answers[String(currentQuestion.id)] === idx
                          ? 'border-primary bg-primary/5 dark:bg-primary/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        answers[String(currentQuestion.id)] === idx
                          ? 'border-primary bg-primary'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {answers[String(currentQuestion.id)] === idx && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{opt}</span>
                      <input
                        type="radio"
                        name={`q-${currentQuestion.id}`}
                        value={idx}
                        checked={answers[String(currentQuestion.id)] === idx}
                        onChange={() => handleAnswerChange(currentQuestion.id, idx)}
                        className="sr-only"
                      />
                    </label>
                  ))}
                </div>
              )}

              {/* Texte libre */}
              {currentQuestion.type === 'text' && (
                <textarea
                  value={answers[String(currentQuestion.id)] || ''}
                  onChange={e => handleAnswerChange(currentQuestion.id, e.target.value)}
                  placeholder="Tapez votre réponse ici…"
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" /> Précédent
            </button>

            {/* Points de navigation */}
            <div className="flex gap-1.5">
              {questions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentQuestionIndex(i)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    answers[String(q.id)] !== undefined
                      ? 'bg-green-500'
                      : i === currentQuestionIndex
                      ? 'bg-primary'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>

            {currentQuestionIndex === questions.length - 1 ? (
              <button
                onClick={() => handleSubmitQuiz(false)}
                disabled={isSubmitting}
                className="flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Envoi…</>
                  : <><CheckCircle className="w-4 h-4" /> Terminer</>}
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90"
              >
                Suivant <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Confirm modal */}
        {showConfirmSubmit && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Soumettre l'évaluation ?</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {answeredCount} / {questions.length} questions répondues.
              </p>
              {answeredCount < questions.length && (
                <p className="text-orange-600 dark:text-orange-400 text-sm mb-4">
                  Attention : {questions.length - answeredCount} question{questions.length - answeredCount > 1 ? 's' : ''} sans réponse.
                </p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmSubmit(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Continuer
                </button>
                <button
                  onClick={() => handleSubmitQuiz(true)}
                  disabled={isSubmitting}
                  className="flex-1 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  Soumettre
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Phase RESULTS ────────────────────────────────────────────────────────
  if (phase === 'results' && lastResult) {
    const attempt = lastResult.attempt;
    const passed = lastResult.passed;
    const pct = Math.round(Number(attempt?.percentage || 0));
    const certificate = lastResult.certificate;

    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Résultat */}
          <div className={`rounded-lg shadow-sm border p-8 text-center mb-6 ${
            passed
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
              passed ? 'bg-green-500' : 'bg-red-500'
            }`}>
              {passed
                ? <CheckCircle className="w-10 h-10 text-white" />
                : <XCircle className="w-10 h-10 text-white" />}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {pct}%
            </h2>
            <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
              {passed ? 'Évaluation réussie !' : 'Score insuffisant'}
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              {passed
                ? `Félicitations ! Vous avez atteint ${pct}% (requis : ${evaluation.passing_score}%).`
                : `Vous avez obtenu ${pct}% — le score minimum est ${evaluation.passing_score}%.`}
            </p>
            {attempt?.score !== undefined && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Score : {attempt.score} / {attempt.total_points} points
              </p>
            )}
          </div>

          {/* Certificat émis */}
          {certificate && lastResult.certificateIssuedNow && (
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg">Certificat émis !</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Vous avez validé tous les modules. Votre certificat est disponible.
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/certificates/${certificate.id}`)}
                  className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium whitespace-nowrap"
                >
                  Voir →
                </button>
              </div>
            </div>
          )}

          {/* Certificat à régler (comptes sans abonnement) : achat sur la plateforme */}
          {lastResult.certificatePaiementRequis && lastResult.certificateLienAchat?.startsWith('https://app.innovactive360.cloud/') && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-6 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <Award className="w-8 h-8 text-amber-600 flex-shrink-0" aria-hidden="true" />
                <p className="flex-1 min-w-[12rem] text-gray-800 dark:text-gray-200 text-sm">{lastResult.certificateEligibleReason}</p>
                <a href={lastResult.certificateLienAchat}
                  className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 text-sm font-medium whitespace-nowrap">
                  Obtenir mon certificat
                </a>
              </div>
            </div>
          )}

          {/* Détail des réponses */}
          {questions.length > 0 && attempt?.answers?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Détail des réponses</h3>
              <div className="space-y-4">
                {questions.map((q, qi) => {
                  const ans = (attempt.answers || []).find(a => String(a.questionId) === String(q.id));
                  const isText = q.type === 'text';
                  const isCorrect = ans?.isCorrect;
                  return (
                    <div key={q.id} className={`p-4 rounded-lg border ${
                      isText
                        ? 'border-gray-200 dark:border-gray-700'
                        : isCorrect
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    }`}>
                      <div className="flex items-start gap-2 mb-2">
                        {!isText && (
                          isCorrect
                            ? <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-1 flex-shrink-0" />
                            : <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-1 flex-shrink-0" />
                        )}
                        <p className="font-medium text-gray-900 dark:text-white text-sm">
                          {qi + 1}. {q.question || q.text}
                        </p>
                      </div>
                      {ans && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">
                          Votre réponse :{' '}
                          {q.type === 'text'
                            ? (ans.answer || <em>Pas de réponse</em>)
                            : (q.options?.[ans.answer] ?? ans.answer)}
                        </p>
                      )}
                      {!isCorrect && !isText && q.options && q.correctAnswer !== undefined && (
                        <p className="text-sm text-green-700 dark:text-green-400 ml-6 mt-1">
                          Bonne réponse : {q.options[q.correctAnswer] ?? q.options[q.correct]}
                        </p>
                      )}
                      {!isCorrect && !isText && q.options && q.correct !== undefined && q.correctAnswer === undefined && (
                        <p className="text-sm text-green-700 dark:text-green-400 ml-6 mt-1">
                          Bonne réponse : {q.options[q.correct]}
                        </p>
                      )}
                      {q.explanation && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 ml-6 mt-1 italic">
                          {q.explanation}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Feedback */}
          {attempt?.feedback && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-300">{attempt.feedback}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate(courseId ? `/course/${courseId}/learn` : '/my-courses')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="w-4 h-4" /> Retour au cours
            </button>
            {!passed && canRetry && (
              <button
                onClick={() => {
                  setLastResult(null);
                  setPhase('intro');
                }}
                className="flex-1 flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-lg hover:bg-primary/90"
              >
                <RotateCcw className="w-4 h-4" /> Réessayer
              </button>
            )}
            {certificate && (
              <button
                onClick={() => navigate('/certificates')}
                className="flex-1 flex items-center justify-center gap-2 bg-yellow-500 text-white px-4 py-3 rounded-lg hover:bg-yellow-600"
              >
                <Award className="w-4 h-4" /> Mes certificats
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TakeQuiz;
